import { endOfDay } from 'date-fns/endOfDay';
import { format as formatDate } from 'date-fns/format';
import { isValid } from 'date-fns/isValid';
import { parse } from 'date-fns/parse';
import { startOfDay } from 'date-fns/startOfDay';
import type { DateRange } from 'types';

/**
 * Types of validation errors that can occur.
 */
export type DateValidationErrorType =
  | 'INVALID_FORMAT'
  | 'INVALID_DATE'
  | 'OUT_OF_RANGE'
  | 'INCOMPLETE';

/**
 * Validation result for date input parsing.
 */
export type DateValidationResult = {
  isValid: boolean;
  parsedDate: Date | undefined;
  errorMessage?: string;
  errorType?: DateValidationErrorType;
  /** If input was in alternate format, this is the corrected display value */
  correctedValue?: string;
};

/**
 * Range validation result for date range inputs.
 */
export type DateRangeValidationResult = {
  isValid: boolean;
  parsedRange: DateRange | undefined;
  fromError?: DateValidationResult;
  toError?: DateValidationResult;
  rangeError?: 'END_BEFORE_START';
  errorMessage?: string;
  errorType?: DateValidationErrorType;
};

/**
 * Configuration for date validation behavior.
 */
export type ValidationConfig = {
  format: string;
  minDate?: Date;
  maxDate?: Date;
  strictMode?: boolean;
};

/**
 * Generates alternate formats to try when the primary format fails.
 * Primarily handles 24-hour to 12-hour time format conversion.
 */
const getAlternateFormats = (primaryFormat: string): string[] => {
  const alternates: string[] = [];

  // If format uses 12-hour time (h:mm a), also accept 24-hour (HH:mm or H:mm)
  if (primaryFormat.includes('h:mm a') || primaryFormat.includes('hh:mm a')) {
    // Replace 12-hour patterns with 24-hour patterns
    alternates.push(primaryFormat.replace(/h{1,2}:mm a/g, 'HH:mm'));
    alternates.push(primaryFormat.replace(/h{1,2}:mm a/g, 'H:mm'));
  }

  // If format uses 24-hour time (HH:mm), also accept 12-hour (h:mm a)
  if (primaryFormat.includes('HH:mm') || primaryFormat.includes('H:mm')) {
    alternates.push(primaryFormat.replace(/H{1,2}:mm/g, 'h:mm a'));
  }

  return alternates;
};

/**
 * Determines if input appears incomplete (user still typing).
 */
export const isIncompleteInput = (input: string, format: string): boolean => {
  const trimmed = input.trim();
  if (!trimmed) {
    return true;
  }

  // Common incomplete patterns for MM/dd/yyyy format
  const incompletePatterns = [
    /^\d{1,2}$/,
    /^\d{1,2}\/$/,
    /^\d{1,2}\/\d{1,2}$/,
    /^\d{1,2}\/\d{1,2}\/$/,
    /^\d{1,2}\/\d{1,2}\/\d{1,3}$/,
    // For yyyy-MM-dd formats
    /^\d{4}$/,
    /^\d{4}-$/,
    /^\d{4}-\d{1,2}$/,
    /^\d{4}-\d{1,2}-$/,
    /^\d{4}-\d{1,2}-\d{1}$/,
  ];

  if (incompletePatterns.some((pattern) => pattern.test(trimmed))) {
    return true;
  }

  // Compare to expected format length
  const formatLength = format.length;
  return trimmed.length < formatLength * 0.8;
};

/**
 * Validates a date string against the expected format and constraints.
 * Supports alternate formats (e.g., 24-hour time when 12-hour is expected).
 */
export const validateDateInput = (
  input: string,
  config: ValidationConfig,
): DateValidationResult => {
  const { format, minDate, maxDate, strictMode = false } = config;
  const trimmed = input.trim();

  // Empty input is valid (no date selected)
  if (!trimmed) {
    return { isValid: true, parsedDate: undefined };
  }

  // Check for incomplete input (still typing)
  if (!strictMode && isIncompleteInput(trimmed, format)) {
    return {
      isValid: false,
      parsedDate: undefined,
      errorType: 'INCOMPLETE',
    };
  }

  // Attempt to parse the date using the primary format
  let parsedDate = parse(trimmed, format, new Date());
  let usedAlternateFormat = false;

  // If primary format failed, try alternate formats
  if (!isValid(parsedDate)) {
    const alternateFormats = getAlternateFormats(format);

    for (const altFormat of alternateFormats) {
      const altParsed = parse(trimmed, altFormat, new Date());
      if (isValid(altParsed)) {
        parsedDate = altParsed;
        usedAlternateFormat = true;
        break;
      }
    }
  }

  // Check if parsing resulted in valid date
  if (!isValid(parsedDate)) {
    return {
      isValid: false,
      parsedDate: undefined,
      errorType: 'INVALID_FORMAT',
      errorMessage: `Invalid date format. Expected: ${format}`,
    };
  }

  // A format with no time parses to midnight, so its bounds are whole days,
  // as in the calendar: a minDate at 15:00 still allows typing that day.
  // Two times of one day format alike unless the format shows a time.
  const hasTime =
    formatDate(new Date(2000, 0, 1), format) !==
    formatDate(new Date(2000, 0, 1, 13, 59, 59, 999), format);
  const lowest = minDate && (hasTime ? minDate : startOfDay(minDate));
  const highest = maxDate && (hasTime ? maxDate : endOfDay(maxDate));

  if (lowest && parsedDate < lowest) {
    return {
      isValid: false,
      parsedDate: undefined,
      errorType: 'OUT_OF_RANGE',
      errorMessage: 'Date is before minimum allowed date',
    };
  }

  if (highest && parsedDate > highest) {
    return {
      isValid: false,
      parsedDate: undefined,
      errorType: 'OUT_OF_RANGE',
      errorMessage: 'Date is after maximum allowed date',
    };
  }

  // If we used an alternate format, provide the corrected value in canonical format
  const correctedValue = usedAlternateFormat
    ? formatDate(parsedDate, format)
    : undefined;

  return { isValid: true, parsedDate, correctedValue };
};

/**
 * Validates a date range input string (format: "date1 - date2").
 */
export const validateDateRangeInput = (
  input: string,
  config: ValidationConfig,
): DateRangeValidationResult => {
  const trimmed = input.trim();

  if (!trimmed) {
    return { isValid: true, parsedRange: undefined };
  }

  // formatValue writes a one-sided range as "<date> - " or " - <date>". After
  // the trim() above that is a bare leading or trailing "-", handled first so
  // the " - " split below doesn't fold the dash into the other side.
  let fromInput: string;
  let toInput: string;
  if (trimmed.startsWith('-')) {
    fromInput = '';
    toInput = trimmed.slice(1).trim();
  } else if (trimmed.endsWith('-')) {
    fromInput = trimmed.slice(0, -1).trim();
    toInput = '';
  } else {
    const parts = trimmed.split(' - ');
    fromInput = parts[0]?.trim() || '';
    toInput = parts[1]?.trim() || '';
  }

  const fromResult = fromInput
    ? validateDateInput(fromInput, config)
    : { isValid: true, parsedDate: undefined };
  const toResult = toInput
    ? validateDateInput(toInput, config)
    : { isValid: true, parsedDate: undefined };

  // Check for incomplete input (still typing)
  const fromIsIncomplete =
    !fromResult.isValid && fromResult.errorType === 'INCOMPLETE';
  const toIsIncomplete =
    !toResult.isValid && toResult.errorType === 'INCOMPLETE';

  if (fromIsIncomplete || toIsIncomplete) {
    return {
      isValid: false,
      parsedRange: undefined,
      errorType: 'INCOMPLETE',
    };
  }

  // Check for errors (excluding incomplete)
  const fromHasError =
    !fromResult.isValid && fromResult.errorType !== 'INCOMPLETE';
  const toHasError = !toResult.isValid && toResult.errorType !== 'INCOMPLETE';

  if (fromHasError || toHasError) {
    const errorMessage = fromHasError
      ? `Start date: ${fromResult.errorMessage}`
      : `End date: ${toResult.errorMessage}`;
    const errorType = fromHasError ? fromResult.errorType : toResult.errorType;

    return {
      isValid: false,
      parsedRange: undefined,
      fromError: fromHasError ? fromResult : undefined,
      toError: toHasError ? toResult : undefined,
      errorMessage,
      errorType,
    };
  }

  // Check range validity (end must be after start)
  if (fromResult.parsedDate && toResult.parsedDate) {
    if (toResult.parsedDate < fromResult.parsedDate) {
      return {
        isValid: false,
        parsedRange: undefined,
        rangeError: 'END_BEFORE_START',
        errorMessage: 'End date must be after start date',
      };
    }
  }

  return {
    isValid: true,
    parsedRange:
      fromResult.parsedDate || toResult.parsedDate
        ? { from: fromResult.parsedDate, to: toResult.parsedDate }
        : undefined,
  };
};
